import Favicon from '@components/Favicon';
import { Spinner } from '@components/spinner';
import { WelcomeModal } from '@components/WelcomeModal';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { SafeAny } from '@models/common';
import { Affiliate, Company, CompanyInformation, TenantList } from '@models/settings';
import { GenericErrorPage } from '@pages/error';
import {
  useAdminConfig,
  useGetCompanyInformation,
  useGetTapfiliate,
  useGetTenantByUser,
  useGetUserInfo,
  useUpdateUserInfo
} from '@queries/settings';
import { useAuth } from '@swyft/auth';
import { ASSESSMENT, CORE, Currency, Feature, PRICING, STORE } from '@swyft/ui';
import { isEqual } from 'lodash-es';
import * as React from 'react';

const IS_WHITE_LABEL = 'IsWhiteLabelClient';

interface IAppState {
  whiteLabelClient: boolean;
  companyInformation: CompanyInformation;
  features: Feature[];
  tapfiliate: Affiliate;
  currency: Currency;
  stripeApiKey: string;
  tenantList: TenantList[];
}

const initState: IAppState = {
  whiteLabelClient: true,
  companyInformation: { companyInfo: {} as Company, isEnabledSubscription: true, isCompanySuspended: false },
  features: [],
  tapfiliate: {} as Affiliate,
  currency: { currencyName: 'EUR', currencySymbol: '€' },
  stripeApiKey: '',
  tenantList: []
};

enum AppActionTypes {
  SetWhiteLabelClient,
  SetCompany,
  SetFeature,
  SetTapfiliate,
  SetCurrency,
  SetStripeApiKey,
  SetTenantList
}

interface ContextProps {
  state: IAppState;
  setWhiteLabelClient?: SafeAny;
}

const AppContext = React.createContext<ContextProps>({ state: initState });
const useAppContext = () => React.useContext(AppContext);

const stateReducer = (state = initState, action: SafeAny) => {
  switch (action.type) {
    case AppActionTypes.SetWhiteLabelClient:
      return {
        ...state,
        whiteLabelClient: action.payload
      };
    case AppActionTypes.SetCompany:
      return {
        ...state,
        companyInformation: action.payload
      };
    case AppActionTypes.SetFeature:
      return {
        ...state,
        features: action.payload
      };
    case AppActionTypes.SetTapfiliate:
      return {
        ...state,
        tapfiliate: action.payload
      };
    case AppActionTypes.SetCurrency:
      return {
        ...state,
        currency: action.payload
      };
    case AppActionTypes.SetStripeApiKey: {
      return {
        ...state,
        stripeApiKey: action.payload
      };
    }
    case AppActionTypes.SetTenantList: {
      return {
        ...state,
        tenantList: action.payload
      };
    }
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
};

const AppProvider = ({ children }: { children: SafeAny }) => {
  const { isAuthenticated, isSignIn } = useAuth();
  const [state, dispatch] = React.useReducer(stateReducer, initState);
  const [noShowAgain, setNoShowAgain] = React.useState<boolean>(false);
  const [openWelcomeModal, setOpenWelcomeModal] = React.useState<boolean>(true);
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

  const setWhiteLabelClient = React.useCallback(
    (whiteLabelClient: boolean) => dispatch({ type: AppActionTypes.SetWhiteLabelClient, payload: whiteLabelClient }),
    [dispatch]
  );
  const setCompany = React.useCallback(
    (company: CompanyInformation) => dispatch({ type: AppActionTypes.SetCompany, payload: company }),
    [dispatch]
  );
  const setFeatures = React.useCallback(
    (features: Feature[]) => dispatch({ type: AppActionTypes.SetFeature, payload: features }),
    [dispatch]
  );
  const setTapfiliate = React.useCallback(
    (tapfiliate: Affiliate) => dispatch({ type: AppActionTypes.SetTapfiliate, payload: tapfiliate }),
    [dispatch]
  );
  const setCurrency = React.useCallback(
    (currency: Currency) => dispatch({ type: AppActionTypes.SetCurrency, payload: currency }),
    [dispatch]
  );
  const setStripeApiKey = React.useCallback(
    (stripeApiKey: string) => dispatch({ type: AppActionTypes.SetStripeApiKey, payload: stripeApiKey }),
    [dispatch]
  );
  const setTenantList = React.useCallback(
    (tenantList: TenantList[]) => dispatch({ type: AppActionTypes.SetTenantList, payload: tenantList }),
    [dispatch]
  );

  const { data: adminConfig, isLoading } = useAdminConfig(isAuthenticated);
  const { data: taffiliateConfig } = useGetTapfiliate(isAuthenticated);
  const {
    data: companyInformation,
    isLoading: isLoadingCompanyInfo,
    isError
  } = useGetCompanyInformation(isAuthenticated);
  const { data: tenantList, isLoading: isLoadingTenantList } = useGetTenantByUser(isAuthenticated);
  const { data: userInfo, isLoading: isLoadingUserInfo } = useGetUserInfo(isAuthenticated);
  const { mutateAsync: updateUserInfo } = useUpdateUserInfo();

  const isShowModalWelcome = false;
  //openWelcomeModal && isAuthenticated && !isMdDown && isSignIn && userInfo && !userInfo.hideAdminIntro;

  React.useEffect(() => {
    if (companyInformation && !isEqual(companyInformation, state.companyInformation)) {
      setCompany(companyInformation);
    }
  }, [companyInformation]);

  React.useEffect(() => {
    if (tenantList && !isEqual(tenantList, state.tenantList)) {
      setTenantList(tenantList);
    }
  }, [tenantList]);

  React.useEffect(() => {
    if (!adminConfig) {
      return;
    }
    const { whiteLabelClient, features, currency, stripeApiKey } = state;
    const { services } = adminConfig;

    const coreFeatures = services.find((s) => s.name === CORE);
    const isWhiteLabelConfig = coreFeatures?.settings?.find((s) => s.name === IS_WHITE_LABEL)?.value || '';
    const assessmentFeatures = services.find((s) => s.name === ASSESSMENT)?.features || [];
    const pricingFeatures = services.find((s) => s.name === PRICING);
    const storeService = services.find((s) => s.name === STORE);

    const storeFeatures = storeService?.features || [];
    const _features = [...storeFeatures, ...assessmentFeatures];

    const storeSettings = storeService?.settings || [];
    const stripeApiKeyConfig = storeSettings.find((s) => s.name === 'Payment.StripeApiKey')?.value || '';

    const currencySetting = pricingFeatures?.settings;

    if (isWhiteLabelConfig === 'False' && whiteLabelClient) {
      setWhiteLabelClient(false);
    }
    if (_features.length && !isEqual(_features, features)) {
      setFeatures(_features);
    }
    if (currencySetting) {
      const currencyName = currencySetting.find((c) => c.name === 'Currency.Name')?.value || '';
      const currencySymbol = currencySetting.find((c) => c.name === 'Currency.Symbol')?.value || '';
      const currencyConfig = {
        currencyName,
        currencySymbol
      };
      if (!isEqual(currencyConfig, currency)) {
        setCurrency(currencyConfig);
      }
    }
    if (stripeApiKeyConfig && stripeApiKey !== stripeApiKeyConfig) {
      setStripeApiKey(stripeApiKeyConfig);
    }
  }, [adminConfig]);

  React.useEffect(() => {
    if (!taffiliateConfig) {
      return;
    }
    setTapfiliate(taffiliateConfig);
  }, [taffiliateConfig]);

  const handleCloseWelcomeModal = () => {
    setOpenWelcomeModal(false);
    if (companyInformation?.companyInfo?.id && noShowAgain && userInfo && !userInfo.hideAdminIntro) {
      updateUserInfo({ hideAdminIntro: true });
    }
  };

  const value = React.useMemo(
    () => ({
      state,
      dispatch,
      setWhiteLabelClient
    }),
    [state, dispatch, setWhiteLabelClient]
  );
  if (isError) {
    return <GenericErrorPage />;
  }

  if (isLoading || isLoadingCompanyInfo || isLoadingTenantList || isLoadingUserInfo) {
    return <Spinner />;
  }

  return (
    <AppContext.Provider value={value}>
      <Favicon />
      {children}
      {isShowModalWelcome && (
        <WelcomeModal
          open={isShowModalWelcome}
          handleClose={handleCloseWelcomeModal}
          noShowAgain={noShowAgain}
          setNoShowAgain={setNoShowAgain}
        />
      )}
    </AppContext.Provider>
  );
};

export { useAppContext, AppProvider };
